import { UserPlus } from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileBackButton } from "../misc/MobileBackButton";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { InlineText } from "../operations/InlineText";
import type { Sale, Team } from "../types";

/**
 * The team of the signed-in user: its name and members. Its administrator
 * renames it and invites colleagues by e-mail; RLS only ever returns this
 * team and its members.
 */
export const TeamPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const content = <TeamContent />;

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold">
              {translate("crm.team.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }

  return (
    <div className="max-w-3xl mx-auto mt-6 flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{translate("crm.team.title")}</h1>
      {content}
    </div>
  );
};

TeamPage.path = "/team";

const TeamContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  // Team admins manage the members (see canAccess)
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "create",
  });
  const { data: teams, isPending: isTeamPending } = useGetList<Team>("teams", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  const { data: members = [], isPending: areMembersPending } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "first_name", order: "ASC" },
    },
  );
  const team = teams?.[0];

  const rename = async (name: string) => {
    if (!team) return;
    try {
      await dataProvider.update("teams", {
        id: team.id,
        data: { name },
        previousData: team,
      });
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
      notify("crm.team.renamed");
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    }
  };

  if (isTeamPending || areMembersPending) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="flex flex-col gap-4 pb-6">
      <Card>
        <CardContent className="flex flex-col gap-1">
          <p className="text-sm text-muted-foreground">
            {translate("crm.team.name")}
          </p>
          {isAdmin && team ? (
            <InlineText
              value={team.name}
              required
              label={translate("crm.team.name")}
              onSave={rename}
              className="text-lg font-semibold"
              displayClassName="text-lg font-semibold"
            />
          ) : (
            <p className="text-lg font-semibold">{team?.name}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {translate("crm.team.members", { smart_count: members.length })}
            </h2>
            {isAdmin && (
              <Button asChild size="sm">
                <Link to="/sales/create">
                  <UserPlus />
                  {translate("crm.team.invite")}
                </Link>
              </Button>
            )}
          </div>
          {isAdmin && (
            <p className="text-sm text-muted-foreground">
              {translate("crm.team.invite_help")}
            </p>
          )}
          <ul className="flex flex-col divide-y" data-testid="team-members">
            {members.map((member) => {
              const row = (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {[member.first_name, member.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </p>
                    <p className="truncate text-sm text-muted-foreground">
                      {member.email}
                    </p>
                  </div>
                  <Badge variant={member.administrator ? "default" : "outline"}>
                    {translate(
                      member.administrator
                        ? "crm.team.role_admin"
                        : "crm.team.role_member",
                    )}
                  </Badge>
                  {member.disabled && (
                    <Badge variant="destructive">
                      {translate("crm.team.disabled")}
                    </Badge>
                  )}
                </div>
              );
              return (
                <li key={member.id}>
                  {isAdmin ? (
                    <Link to={`/sales/${member.id}`} className="block">
                      {row}
                    </Link>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
